import { useState } from 'react';
import { Box, Button, FormControlLabel, Radio, RadioGroup, Stack, TextField, Tooltip, Typography } from '@mui/material';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import SendOutlined from '@mui/icons-material/SendOutlined';
import { formatJordan, type Instruction } from '@serb/shared';
import { useNotice } from '../../components/NoticeProvider';
import { useAction } from '../../lib/useAction';
import type { Approval } from './sample';

/** What you can say to Claude about a card */
export type Talk = 'question' | 'once' | 'rule';

type Props = {
  a: Approval;
  /** what is typed in the box; the card keeps it so Reject can use it as the reason */
  text: string;
  onText: (text: string) => void;
  onSend: (id: string, text: string, kind: Talk) => Promise<void>;
};

const WHEN = { weekday: undefined, year: undefined } as const;

/** One thing you wrote to Claude, and under it what Claude answered (or that it has not yet) */
function Exchange({ i }: { i: Instruction }) {
  const label = i.question ? 'You asked' : i.rule ? 'You told Claude (as a rule)' : 'You told Claude';
  return (
    <Stack spacing={0.5}>
      <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'action.hover' }}>
        <Typography variant="caption" color="text.secondary">
          {label} · {formatJordan(i.at, WHEN)}
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>
          {i.text}
        </Typography>
      </Box>
      <Box sx={{ p: 1, borderRadius: 2, border: 1, borderColor: i.answered ? 'primary.light' : 'divider', ml: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Claude{i.answeredAt ? ` · ${formatJordan(i.answeredAt, WHEN)}` : ''}
        </Typography>
        {i.answered ? (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>
            {i.answer || (i.question ? 'Claude marked this as answered but wrote no answer. Ask again.' : 'Done.')}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Has not answered yet. It is woken within a minute.
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

/**
 * Talk to Claude about one card: ask a question (Claude answers here and changes nothing), tell it what to change, or
 * give it a lasting rule. Once the card is decided only questions make sense, so that is all it offers then.
 */
export function CardChat({ a, text, onText, onSend }: Props) {
  const notice = useNotice();
  const pending = a.status === 'pending';
  const [kind, setKind] = useState<Talk>(pending ? 'once' : 'question');
  const mode: Talk = pending ? kind : 'question';

  const send = useAction(() => onSend(a.id, text.trim(), mode), {
    success:
      mode === 'question'
        ? 'Sent. Claude will answer here, usually within a minute or two.'
        : `Saved for Claude. It is woken for it within a minute. Nothing was sent.${mode === 'rule' ? ' It will also be kept as a rule in SERB Mind → Learned Lessons.' : ''}`,
    onDone: () => onText(''),
  });
  const submit = () =>
    text.trim()
      ? send.run(undefined)
      : notice(
          mode === 'question' ? 'Write your question first.' : 'Write what Claude should change first.',
          'warning',
        );

  return (
    <Stack spacing={1.25}>
      <Typography sx={{ fontWeight: 700 }}>Talk to Claude about this card</Typography>
      {a.instructions?.map((i) => (
        <Exchange key={i.id} i={i} />
      ))}
      <TextField
        multiline
        minRows={3}
        placeholder={
          mode === 'question'
            ? 'Ask Claude something about this card…'
            : 'Tell Claude what to change or do differently. To reject, write why here and press Reject…'
        }
        value={text}
        onChange={(e) => onText(e.target.value)}
        slotProps={{ htmlInput: { 'aria-label': `Your message to Claude for ${a.srb} ${a.title}` } }}
      />
      {pending && (
        <RadioGroup row value={kind} onChange={(e) => setKind(e.target.value as Talk)}>
          <FormControlLabel value="question" control={<Radio size="small" />} label="Ask a question" />
          <FormControlLabel value="once" control={<Radio size="small" />} label="Change this time only" />
          <FormControlLabel
            value="rule"
            control={<Radio size="small" />}
            label={
              <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                <span>Save as a rule (SERB Mind → Learned Lessons)</span>
                <Tooltip title="Claude will follow this every time from now on, not just here.">
                  <InfoOutlined sx={{ fontSize: 16 }} color="disabled" />
                </Tooltip>
              </Stack>
            }
          />
        </RadioGroup>
      )}
      <Box>
        <Button variant="outlined" startIcon={<SendOutlined />} disabled={send.busy} onClick={submit}>
          {mode === 'question' ? 'Ask Claude' : 'Send to Claude'}
        </Button>
      </Box>
    </Stack>
  );
}
